/**
 * Sentry integration for error tracking
 */

// In a real app, you would import from @sentry/browser
// import * as Sentry from '@sentry/browser';

// Mock Sentry for demo purposes
const Sentry = {
  init: (options: any) => {
    console.log('[Sentry] Initialized with options:', options);
  },
  captureException: (error: Error, context?: any) => {
    console.log('[Sentry] Captured exception:', error, context);
  },
  captureMessage: (message: string, level?: string) => {
    console.log(`[Sentry] Captured message (${level || 'info'}):`, message);
  },
  setUser: (user: any) => {
    console.log('[Sentry] Set user:', user);
  },
  setTag: (key: string, value: string) => {
    console.log(`[Sentry] Set tag ${key}:`, value);
  },
  setTags: (tags: Record<string, string>) => {
    console.log('[Sentry] Set tags:', tags);
  },
  setContext: (name: string, context: any) => {
    console.log(`[Sentry] Set context ${name}:`, context);
  },
  configureScope: (callback: (scope: any) => void) => {
    console.log('[Sentry] Configure scope');
    callback({ setTag: () => {}, setUser: () => {} });
  },
  withScope: (callback: (scope: any) => void) => {
    console.log('[Sentry] With scope');
    callback({ setTag: () => {}, setUser: () => {} });
  }
};

/**
 * Initialize Sentry
 */
export const initSentry = () => {
  if (process.env.NODE_ENV !== 'production') {
    return;
  }
  
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.MODE,
    release: import.meta.env.VITE_APP_VERSION || '1.0.0',
    integrations: [
      // new Sentry.BrowserTracing(),
      // new Sentry.Replay()
    ],
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });
};

/**
 * Set user information in Sentry
 */
export const setSentryUser = (user: { id: string; email?: string; username?: string }) => {
  if (process.env.NODE_ENV !== 'production') {
    return;
  }
  
  Sentry.setUser({
    id: user.id,
    email: user.email,
    username: user.username
  });
};

/**
 * Clear user information from Sentry
 */
export const clearSentryUser = () => {
  if (process.env.NODE_ENV !== 'production') {
    return;
  }
  
  Sentry.setUser(null);
};

/**
 * Capture exception with Sentry
 */
export const captureException = (error: Error, context?: Record<string, any>) => {
  if (process.env.NODE_ENV !== 'production') {
    console.error('[Error]', error, context);
    return;
  }
  
  Sentry.withScope(scope => {
    if (context) {
      Object.entries(context).forEach(([key, value]) => {
        scope.setTag(key, String(value));
      });
    }
    
    Sentry.captureException(error);
  });
};

/**
 * Capture message with Sentry
 */
export const captureMessage = (message: string, level: 'info' | 'warning' | 'error' = 'info', context?: Record<string, any>) => {
  if (process.env.NODE_ENV !== 'production') {
    console.log(`[${level.toUpperCase()}]`, message, context);
    return;
  }
  
  Sentry.withScope(scope => {
    if (context) {
      Object.entries(context).forEach(([key, value]) => {
        scope.setTag(key, String(value));
      });
    }
    
    Sentry.captureMessage(message, level);
  });
};

/**
 * Set global tags for Sentry
 */
export const setTags = (tags: Record<string, string>) => {
  if (process.env.NODE_ENV !== 'production') {
    return;
  }
  
  Sentry.setTags(tags);
};

export default {
  initSentry,
  setSentryUser,
  clearSentryUser,
  captureException,
  captureMessage,
  setTags
};